import {Post, TreeNode} from "../../../types/Post";
import {
  addPost,
  changeCoverImage,
  createCategory,
  deleteCategory,
  deletePostById,
  moveCategory,
  renameCategory,
  updatePostById,
} from "../api";
import {CONTENT_MANAGEMENT_MODAL_CONTENT} from "../components/ContentManagementModalContent";

type UseContentManagementMutationsProps = {
  userToken: string;
  selectedCategory: TreeNode | null;
  editingPost: Post | null;
  refresh: () => Promise<void>;
  setSelectedCategory: (value: TreeNode | null) => void;
  setDialogContent: (value: CONTENT_MANAGEMENT_MODAL_CONTENT | "") => void;
};

export const useContentManagementMutations = ({
  userToken,
  selectedCategory,
  editingPost,
  refresh,
  setSelectedCategory,
  setDialogContent,
}: UseContentManagementMutationsProps) => {
  const onSaveRenameCategory = async (name: string) => {
    setDialogContent(CONTENT_MANAGEMENT_MODAL_CONTENT.LOADING);
    try {
      await renameCategory({token: userToken, id: selectedCategory.id, name});
      await refresh();
    } catch (error) {
      console.error(error);
    } finally {
      setDialogContent("");
    }
  };

  const onSaveChangeCoverImage = async (coverImageUrl: string) => {
    setDialogContent(CONTENT_MANAGEMENT_MODAL_CONTENT.LOADING);
    try {
      await changeCoverImage({token: userToken, id: selectedCategory.id, coverImageUrl});
      await refresh();
    } catch (error) {
      console.error(error);
    } finally {
      setDialogContent("");
    }
  };

  const onAddCategory = async (newCategory) => {
    setDialogContent(CONTENT_MANAGEMENT_MODAL_CONTENT.LOADING);
    try {
      const {name, level, parentId} = newCategory;
      await createCategory({token: userToken, name, level, parentId});
      await refresh();
    } catch (error) {
      console.error(error);
    } finally {
      setDialogContent("");
    }
  };

  const onMoveCategory = async ({id, level, parentId}) => {
    setDialogContent(CONTENT_MANAGEMENT_MODAL_CONTENT.LOADING);
    try {
      await moveCategory({token: userToken, id, level, parentId});
      await refresh();
      setSelectedCategory(null);
    } catch (error) {
      console.error(error);
    } finally {
      setDialogContent("");
    }
  };

  const onDeleteCategory = async (id: string) => {
    setDialogContent(CONTENT_MANAGEMENT_MODAL_CONTENT.LOADING);
    try {
      await deleteCategory({token: userToken, id});
      await refresh();
      setSelectedCategory(null);
    } catch (error) {
      console.error(error);
    } finally {
      setDialogContent("");
    }
  };

  const createPost = async (post: Post) => {
    try {
      setDialogContent(CONTENT_MANAGEMENT_MODAL_CONTENT.LOADING);
      if (!editingPost) {
        await addPost({
          token: userToken,
          post: {
            title: post.title,
            content: post.content,
            detailCategoryId: post.detailCategoryId,
            coverImageUrl: post.coverImageUrl,
          },
        });
      } else {
        await updatePostById({
          token: userToken,
          post: {
            id: post.id,
            title: post.title,
            content: post.content,
            detailCategoryId: post.detailCategoryId,
            coverImageUrl: post.coverImageUrl,
          },
        });
      }
      await refresh();
    } catch (error) {
      console.error(error);
    } finally {
      setDialogContent("");
    }
  };

  const onConfirmDeletePost = async () => {
    setDialogContent(CONTENT_MANAGEMENT_MODAL_CONTENT.LOADING);
    try {
      await deletePostById({token: userToken, id: editingPost.id});
      await refresh();
    } catch (error) {
      console.error(error);
    } finally {
      setDialogContent("");
    }
  };

  return {
    onSaveRenameCategory,
    onSaveChangeCoverImage,
    onAddCategory,
    onMoveCategory,
    onDeleteCategory,
    createPost,
    onConfirmDeletePost,
  };
};
